import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, ChevronRight, TrendingUp, BookOpen, Users, Lightbulb, Home } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Avatar, LevelBadge, ProgressBar, EmptyState } from "@/components/shared";
import { SKILL_STATUSES, STATUS_STYLE, progressForSkills } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Assess() {
  const [classes, setClasses] = useState([]);
  const [activeClass, setActiveClass] = useState(null);
  const [dancers, setDancers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [levels, setLevels] = useState([]);
  const [skills, setSkills] = useState([]);
  const [cats, setCats] = useState([]);
  const [selDancer, setSelDancer] = useState(null);
  const [prog, setProg] = useState({ styles: [], skills: [] });
  const [view, setView] = useState("curriculum"); // curriculum | progress | dancer
  const [classProg, setClassProg] = useState({}); // dancer_id -> pct

  useEffect(() => {
    api.get("/classes").then((r) => { setClasses(r.data); if (r.data[0]) setActiveClass(r.data[0]); });
    api.get("/people/dancers").then((r) => setDancers(r.data));
    api.get("/enrollments").then((r) => setEnrollments(r.data));
    api.get("/curriculum/levels").then((r) => setLevels(r.data));
    api.get("/curriculum/skills").then((r) => setSkills(r.data));
    api.get("/curriculum/categories").then((r) => setCats(r.data));
  }, []);

  const dancerMap = Object.fromEntries(dancers.map((d) => [d.id, d]));
  const levelMap = Object.fromEntries(levels.map((l) => [l.id, l]));
  const catMap = Object.fromEntries(cats.map((c) => [c.id, c.name]));
  const roster = activeClass ? enrollments.filter((e) => e.class_id === activeClass.id && e.status !== "Withdrawn") : [];

  const classStyle = activeClass?.style_id;
  const classLevel = activeClass?.level_id;
  const levelSkills = skills.filter((s) => s.level_id === classLevel);
  const reqIds = levelSkills.filter((s) => s.required).map((s) => s.id);
  const progMap = Object.fromEntries(prog.skills.map((p) => [p.skill_id, p.status]));

  const loadProg = (did) => api.get(`/curriculum/progress/${did}`).then((r) => setProg(r.data));
  const openDancer = (did) => { setSelDancer(did); setView("dancer"); loadProg(did); };

  // class progress: fetch each roster dancer's % for the class level
  useEffect(() => {
    if (view !== "progress" || !activeClass) return;
    (async () => {
      const map = {};
      for (const e of roster) {
        try {
          const r = await api.get(`/curriculum/progress/${e.dancer_id}`);
          map[e.dancer_id] = progressForSkills(r.data.skills, reqIds);
        } catch { map[e.dancer_id] = 0; }
      }
      setClassProg(map);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, activeClass]);

  const setStatus = async (skillId, status) => {
    try {
      const res = await api.post("/curriculum/assess", { dancer_id: selDancer, skill_id: skillId, level_id: classLevel, status });
      await loadProg(selDancer);
      if (res.data.ready_for_advancement) toast.success("🎉 All required skills mastered — ready for advancement!");
      else toast.success("Progress updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const recommend = async () => {
    const sp = prog.styles.find((s) => s.style_id === classStyle);
    const curLevelId = sp?.current_level_id || classLevel;
    const styleLevels = levels.filter((l) => l.style_id === classStyle).sort((a, b) => a.display_order - b.display_order);
    const idx = styleLevels.findIndex((l) => l.id === curLevelId);
    const next = idx >= 0 ? styleLevels[idx + 1] : null;
    if (!next) { toast.error("No next level defined — this dancer is at the top level for this style."); return; }
    try {
      await api.post("/curriculum/advancement-requests", { dancer_id: selDancer, style_id: classStyle, from_level_id: curLevelId, to_level_id: next.id });
      toast.success(`Recommended advancement to ${next.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const VIEWS = [
    { id: "curriculum", label: "Curriculum", icon: BookOpen },
    { id: "progress", label: "Class Progress", icon: Users },
    { id: "dancer", label: "Individual Dancer", icon: ClipboardCheck },
  ];

  return (
    <div className="stagger-in">
      <PageHeader title="Track Skills" subtitle="Curriculum, class progress and dancer assessments in one place." testid="assess-page" />

      <div className="flex flex-wrap gap-2 mb-4">
        {classes.map((c) => (
          <button key={c.id} data-testid={`assess-class-${c.id}`} onClick={() => { setActiveClass(c); setSelDancer(null); }}
            className={`px-4 py-2 rounded-full font-semibold text-sm border ${activeClass?.id === c.id ? "bg-black text-white border-black" : "bg-white"}`}>{c.name}</button>
        ))}
      </div>

      {activeClass && (
        <p className="text-sm text-muted-foreground mb-4">
          {activeClass.name} · <LevelBadge name={levelMap[classLevel]?.name} color={levelMap[classLevel]?.color} small />
        </p>
      )}

      {/* View switcher */}
      <div className="flex overflow-x-auto max-w-full sm:inline-flex bg-white rounded-full p-1 border border-gray-100 mb-6">
        {VIEWS.map((v) => (
          <button key={v.id} data-testid={`assess-view-${v.id}`} onClick={() => setView(v.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap shrink-0 transition-colors ${view === v.id ? "bg-teal text-white" : "text-gray-600 hover:bg-teal-light"}`}>
            <v.icon className="w-4 h-4" />{v.label}
          </button>
        ))}
      </div>

      {/* Curriculum view */}
      {view === "curriculum" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-head font-bold mb-1">What to teach — {levelMap[classLevel]?.name || "this level"}</h3>
          <p className="text-sm text-muted-foreground mb-4">Skills and expectations for this class's level.</p>
          {levelSkills.length === 0 ? <EmptyState icon={BookOpen} title="No curriculum for this level yet" /> :
            <div className="space-y-3">
              {levelSkills.map((sk) => (
                <div key={sk.id} data-testid={`curr-skill-${sk.id}`} className="p-4 rounded-xl bg-background">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold">{sk.name}<span className="text-xs text-muted-foreground ml-2">{catMap[sk.category_id]}</span></p>
                    <Badge className={sk.required ? "bg-teal text-white rounded-full" : "bg-gray-100 text-gray-600 rounded-full"}>{sk.required ? "Required" : "Optional"}</Badge>
                  </div>
                  {sk.definition && <p className="text-sm mt-2">{sk.definition}</p>}
                  {sk.teaching_tips && <p className="text-sm mt-2 flex gap-1.5 text-teal-dark"><Lightbulb className="w-4 h-4 shrink-0 mt-0.5" /><span><b>Teaching tip:</b> {sk.teaching_tips}</span></p>}
                  {sk.home_practice && <p className="text-sm mt-1.5 flex gap-1.5 text-muted-foreground"><Home className="w-4 h-4 shrink-0 mt-0.5" /><span><b>Home practice:</b> {sk.home_practice}</span></p>}
                </div>
              ))}
            </div>}
        </div>
      )}

      {/* Class progress view */}
      {view === "progress" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-head font-bold mb-4">How the class is progressing</h3>
          {roster.length === 0 ? <p className="text-sm text-muted-foreground">No dancers enrolled.</p> :
            <div className="space-y-3">
              {roster.map((e) => {
                const d = dancerMap[e.dancer_id]; const pct = classProg[e.dancer_id] ?? 0;
                return (
                  <button key={e.id} data-testid={`progress-row-${e.dancer_id}`} onClick={() => openDancer(e.dancer_id)} className="w-full text-left flex items-center gap-3 p-3 rounded-xl hover:bg-background transition-colors">
                    <Avatar first={d?.first_name} last={d?.last_name} size={38} />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between"><span className="font-semibold text-sm">{d?.first_name} {d?.last_name}</span><span className="text-xs text-muted-foreground">{pct}%</span></div>
                      <div className="mt-1.5"><ProgressBar value={pct} color={levelMap[classLevel]?.color} height={10} /></div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                );
              })}
            </div>}
        </div>
      )}

      {/* Individual dancer view */}
      {view === "dancer" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 h-fit">
            <h3 className="font-head font-bold mb-4">Roster</h3>
            {roster.length === 0 ? <p className="text-sm text-muted-foreground">No dancers enrolled.</p> :
              <div className="space-y-2">
                {roster.map((e) => {
                  const d = dancerMap[e.dancer_id];
                  return (
                    <button key={e.id} data-testid={`assess-roster-${e.dancer_id}`} onClick={() => openDancer(e.dancer_id)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-colors ${selDancer === e.dancer_id ? "border-teal bg-teal-light" : "border-gray-100 hover:bg-background"}`}>
                      <span className="flex items-center gap-3"><Avatar first={d?.first_name} last={d?.last_name} size={34} /><span className="font-semibold text-sm">{d?.first_name} {d?.last_name}</span></span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>}
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
            {!selDancer ? <EmptyState icon={ClipboardCheck} title="Select a dancer" hint="Pick a dancer from the roster to assess." /> :
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-head font-bold text-lg">{dancerMap[selDancer]?.first_name} {dancerMap[selDancer]?.last_name}</h3>
                    <div className="mt-1"><LevelBadge name={levelMap[classLevel]?.name} color={levelMap[classLevel]?.color} small /></div>
                  </div>
                  <Button data-testid="recommend-advancement-button" onClick={recommend} className="rounded-full bg-teal hover:bg-teal-dark"><TrendingUp className="w-4 h-4 mr-2" />Recommend</Button>
                </div>
                <ProgressBar value={progressForSkills(prog.skills, reqIds)} color={levelMap[classLevel]?.color} />
                <p className="text-sm text-muted-foreground mt-2 mb-5">{progressForSkills(prog.skills, reqIds)}% of required skills mastered</p>
                {levelSkills.length === 0 ? <p className="text-sm text-muted-foreground">This class's level has no skills defined.</p> :
                  <div className="space-y-3">
                    {levelSkills.map((sk) => {
                      const cur = progMap[sk.id] || "Not Started";
                      return (
                        <div key={sk.id} data-testid={`assess-skill-${sk.id}`} className="p-3.5 rounded-xl bg-background">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm">{sk.name}{sk.required && <span className="text-teal ml-1">*</span>}</span>
                            <Badge className="rounded-full text-white text-[10px]" style={{ background: STATUS_STYLE[cur].dot }}>{cur}</Badge>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {SKILL_STATUSES.map((st) => (
                              <button key={st} data-testid={`set-status-${sk.id}-${st.replace(/\s+/g, "-")}`} onClick={() => setStatus(sk.id, st)}
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${cur === st ? "text-white border-transparent" : "bg-white text-gray-600 hover:border-teal"}`}
                                style={cur === st ? { background: STATUS_STYLE[st].dot } : {}}>{st}</button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>}
              </>}
          </div>
        </div>
      )}
    </div>
  );
}
